/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDebounce } from '@opengeoweb/shared';
import { defaultTimeSpan, defaultTimeStep } from '@opengeoweb/time-slider';
import { SYNCGROUPS_TYPE_SETTIME } from './constants';
import { syncGroupAddSource, syncGroupRemoveSource } from './reducer';
import {
  getAllTargetGroupsForSource,
  getSynchronizationGroup,
} from './selectors';
import type {
  SyncGroupRemoveSourcePayload,
  SyncGroupsAddSourcePayload,
} from './types';
import type { TimeSliderStartEndStepCurrent } from '../../map/map/types';
import type { CoreAppStore } from '../../types';
import {
  getSyncedStartCenterEndStepCurrentTime,
  getSynchronizationGroupStore,
  getTime,
} from '../selectors';

/**
 * This registers a new syncgroup source for given panelId. The source is not synced to any group by default.
 * When the user links the source with a group, this hook returns an object with the timeslider state (start, end, step and current time) for the panel (if effectively linked).
 *
 * How it works:
 * - Registers/unregisters the source on mount/unmount.
 * - Returns `{ start, end, step, current }` when linked.
 * - Uses linked map selected time as `current` when available.
 * - Falls back to sync source "now" timestamp when
 *   synced state is not initialized yet.
 *
 * @param panelId panelId to register as sync source
 * @returns timeslider state
 */
export const useSyncgroupTimeState = (
  panelId: string,
  debounceMs = 100,
): TimeSliderStartEndStepCurrent | undefined => {
  const dispatch = useDispatch();
  const defaultNowTime = React.useRef(Math.round(Date.now() / 1000));

  const syncGroupsAddSourceCallBack = React.useCallback(
    (payload: SyncGroupsAddSourcePayload): void => {
      dispatch(syncGroupAddSource(payload));
    },
    [dispatch],
  );
  const syncGroupRemoveSourceCallback = React.useCallback(
    (payload: SyncGroupRemoveSourcePayload): void => {
      dispatch(syncGroupRemoveSource(payload));
    },
    [dispatch],
  );

  // Register
  useEffect(() => {
    syncGroupsAddSourceCallBack({
      id: panelId,
      type: [SYNCGROUPS_TYPE_SETTIME],
    });
    return (): void => {
      syncGroupRemoveSourceCallback({ id: panelId });
    };
  }, [panelId, syncGroupsAddSourceCallBack, syncGroupRemoveSourceCallback]);

  const syncGroupTimeSliderCenterStartEndStep = useSelector(
    (store: CoreAppStore) =>
      getSyncedStartCenterEndStepCurrentTime(store, panelId),
  );
  const syncSourceTime = useSelector((store: CoreAppStore) =>
    getTime(getSynchronizationGroupStore(store), panelId),
  );

  const syncTargets = useSelector((store: CoreAppStore) =>
    getAllTargetGroupsForSource(store, panelId),
  );

  const isSyncgroupLinked = useSelector((store: CoreAppStore) =>
    syncTargets.some((groupId) => {
      const group = getSynchronizationGroup(store, groupId);
      if (group?.type !== SYNCGROUPS_TYPE_SETTIME) {
        return false;
      }
      return group.targets.allIds.some(
        (targetId) =>
          targetId !== panelId && group.targets.byId[targetId]?.linked,
      );
    }),
  );

  const linkedMapIdsInTimeGroup = useSelector((store: CoreAppStore) => {
    for (const groupId of syncTargets) {
      const group = getSynchronizationGroup(store, groupId);
      if (group?.type === SYNCGROUPS_TYPE_SETTIME) {
        for (const targetId of group.targets.allIds) {
          if (
            targetId !== panelId &&
            group.targets.byId[targetId]?.linked &&
            !!store.webmap?.byId?.[targetId]
          ) {
            return targetId;
          }
        }
      }
    }
    return undefined;
  });
  const linkedMapSelectedTime = useSelector((store: CoreAppStore) => {
    if (!linkedMapIdsInTimeGroup) {
      return undefined;
    }
    const selectedTime = store.webmap?.byId?.[
      linkedMapIdsInTimeGroup
    ]?.dimensions?.find((dimension) => dimension.name === 'time')?.currentValue;
    if (!selectedTime) {
      return undefined;
    }
    const selectedTimeAsUnix = Date.parse(selectedTime) / 1000;
    if (!Number.isFinite(selectedTimeAsUnix)) {
      return undefined;
    }
    return Math.round(selectedTimeAsUnix);
  });

  const syncSourceCurrentTime = syncSourceTime?.currentTime
    ? Date.parse(syncSourceTime.currentTime) / 1000
    : undefined;
  const roundedSyncSourceCurrentTime =
    syncSourceCurrentTime !== undefined &&
    Number.isFinite(syncSourceCurrentTime)
      ? Math.round(syncSourceCurrentTime)
      : undefined;

  const fallbackCurrentTime =
    linkedMapSelectedTime ??
    roundedSyncSourceCurrentTime ??
    defaultNowTime.current;

  const fallbackState = isSyncgroupLinked
    ? ({
        start: fallbackCurrentTime - defaultTimeSpan / 2,
        end: fallbackCurrentTime + defaultTimeSpan / 2,
        step: defaultTimeStep,
        current: fallbackCurrentTime,
      } as TimeSliderStartEndStepCurrent)
    : undefined;

  const syncedState = isSyncgroupLinked
    ? syncGroupTimeSliderCenterStartEndStep || fallbackState
    : undefined;

  const mapFallbackState =
    linkedMapSelectedTime !== undefined
      ? {
          start: linkedMapSelectedTime - defaultTimeSpan / 2,
          end: linkedMapSelectedTime + defaultTimeSpan / 2,
          step: defaultTimeStep,
          current: linkedMapSelectedTime,
        }
      : undefined;

  const baseState = syncedState || mapFallbackState;
  const stateWithAuthoritativeCurrent =
    baseState && linkedMapSelectedTime !== undefined
      ? { ...baseState, current: linkedMapSelectedTime }
      : baseState;

  return useDebounce(stateWithAuthoritativeCurrent, debounceMs);
};
